import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LaasOption } from 'src/app/core/models/laas-option';
import { DateTableColumn } from 'src/app/core/models/date-table-column';
import { isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { PlantService } from '../../services/plant-service';
import { DeliveryOrderService } from '../../services/delivery-order.service';
import { ExpiredControlService } from '../../services/expired-control.service';
import { ExpiredControlSearchRequest, ExpiredControlSend311Request } from 'src/app/core/models/transport-operation/expired-control-request';
import { ExpiredControlSearchResponse, ExpiredControlSearchStock, ExpiredControlSearchStockDetail } from 'src/app/core/models/transport-operation/expired-control-response';

@Component({
  selector: 'laas-expired-control',
  templateUrl: './expired-control.component.html',
  styleUrls: ['./expired-control.component.scss']
})
export class ExpiredControlComponent extends SessionBaseComponent implements OnInit {

  readonly permissionCode: PermissionConstants = PermissionConstants.ViewExpiredControl;
  currentLang: string = this.laasSetting.language;

  @ViewChild('dataTable') dataTable!: DataTableComponent;

  isSearchFormOpen: boolean = true;
  searchForm: FormGroup;
  isLoading: boolean = false;
  submitted: boolean = false;
  submittedSend311: boolean = false;
  searchResults: any[] = [];
  selectedRows: any[] = [];
  columns: DateTableColumn[] = [];
  detailColumns: DateTableColumn[] = [];

  // Dropdown options
  plantOptions: any[] = [];
  storageLocationOptions: Array<LaasOption> = [];
  statusOptions: Array<LaasOption> = [];

  constructor(
    private fb: FormBuilder,
    injector: Injector,
    private plantService: PlantService,
    private doService: DeliveryOrderService,
    private expiredControlService: ExpiredControlService
  ) {
    super(injector);

    this.searchForm = this.fb.group({
      plant: [null, Validators.required],
      specLocType: [null, Validators.required],
      sku: [null],
      expiredStatus: [null, Validators.required],
      nameOfPerson: [null],
      costCenter: [null],
      employeeID: [null]
    });

    this.columns = [
      { field: 'plant', header: 'ExpiredControl.MainTable.Plant', sort: true },
      { field: 'fromSpecLocType', header: 'ExpiredControl.MainTable.FromSpecLocType', sort: true },
      { field: 'sku', header: 'ExpiredControl.MainTable.SKU', sort: true },
      { field: 'totalQty', header: 'ExpiredControl.MainTable.TotalQty', sort: true },
      { field: 'toSpecLocType', header: 'ExpiredControl.MainTable.ToSpecLocType', sort: true }
    ];

    this.detailColumns = [
      { field: 'sku', header: 'ExpiredControl.DetailTable.SKU', sort: true },
      { field: 'qty', header: 'ExpiredControl.DetailTable.Qty', sort: true },
      { field: 'uId', header: 'ExpiredControl.DetailTable.UID', sort: true }
    ];
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    // Initialize status options with i18n support
    this.initStatusOptions();
    this.translateService.onLangChange.subscribe(() => {
      this.initStatusOptions();
    });

    // Load plant options (廠區)
    this.plantService.getPlantOptions().subscribe({
      next: (result) => {
        const uniquePlantIds = Array.from(new Set(result.map((x: { plantId: any }) => x.plantId)));
        this.plantOptions = uniquePlantIds.map(plantId => ({
          label: plantId,
          value: plantId
        }));
      }
    });

    // Load storage location options (庫別)
    this.doService.getSpecLocType().subscribe({
      next: (result) => {
        const seen = new Set<string>();
        this.storageLocationOptions = result.specLocTypeList
          .filter((x: { specLocType: any; }) => {
            if (seen.has(x.specLocType)) return false;
            seen.add(x.specLocType);
            return true;
          })
          .map((x: { specLocType: any; }) => ({
            label: x.specLocType,
            value: x.specLocType
          }));
      }
    });
  }

  private initStatusOptions(): void {
    this.statusOptions = [
      { label: this.translateService.instant('ExpiredControl.StatusOptions.Expired'), value: '00' },
      { label: this.translateService.instant('ExpiredControl.StatusOptions.NotExpiredInExpiredStorage'), value: 'D' },
      { label: this.translateService.instant('ExpiredControl.StatusOptions.ExpiringSoon30'), value: '30' },
      { label: this.translateService.instant('ExpiredControl.StatusOptions.ExpiringSoon60'), value: '60' }
    ];
  }

  searchBtnClick(): void {
    this.submitted = true;
    this.submittedSend311 = false;

    const plantCtrl = this.searchForm.controls['plant'];
    const specLocTypeCtrl = this.searchForm.controls['specLocType'];
    const expiredStatusCtrl = this.searchForm.controls['expiredStatus'];

    if (plantCtrl.invalid || specLocTypeCtrl.invalid || expiredStatusCtrl.invalid) {
      this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFields'));
      return;
    }

    if (expiredStatusCtrl.value === 'D' && !String(specLocTypeCtrl.value || '').toUpperCase().endsWith('D')) {
      this.toastService.warn(this.translateService.instant('ExpiredControl.Message.OnlyExpiredSpecLocType'));
      return;
    }

    this.dataTable?.setCurrentPage(0);
    this.searchResult();
  }

  searchResult(): void {
    this.isLoading = true;
    this.selectedRows.length = 0;

    const formValue = this.searchForm.value;

    const request: ExpiredControlSearchRequest = {
      plant: formValue.plant,
      specLocType: formValue.specLocType,
      sku: formValue.sku || undefined,
      expiredStatus: formValue.expiredStatus
    };

    this.expiredControlService.searchExpiredInventory(request)
      .subscribe({
        next: (result: ExpiredControlSearchResponse) => {
          this.isSearchFormOpen = false;
          this.searchResults = [];

          if (!isNullOrEmpty(result) && !isNullOrEmpty(result.stocks)) {
            // One header row per SKU; details pre-loaded for expansion
            this.searchResults = result.stocks.map((stock: ExpiredControlSearchStock) => ({
              plant: result.plant,
              fromSpecLocType: result.fromSpecLocType,
              sku: stock.sku,
              totalQty: stock.totalQty,
              toSpecLocType: stock.stockDs?.[0]?.toSpecLocType || '',
              details: (stock.stockDs || []).map((detail: ExpiredControlSearchStockDetail) => ({
                sku: stock.sku,
                qty: detail.qty,
                fromLoc: detail.fromLoc,
                toLoc: detail.toLoc,
                toSpecLocType: detail.toSpecLocType,
                uId: detail.uId
              }))
            }));
          }

          this.dataTable.updateTableData(this.searchResults);
          this.isLoading = false;
        },
        error: (err: any) => {
          this.searchResults = [];
          this.dataTable.updateTableData(this.searchResults);
          this.toastErrorMessage(err.error);
          this.isLoading = false;
        }
      });
  }

  resetBtnClick(): void {
    this.searchForm.reset({
      plant: null,
      specLocType: null,
      sku: null,
      expiredStatus: null,
      nameOfPerson: null,
      costCenter: null,
      employeeID: null
    });
    this.submitted = false;
    this.submittedSend311 = false;
  }

  send311BtnClick(): void {
    this.submittedSend311 = true;

    const plantCtrl = this.searchForm.controls['plant'];
    const specLocTypeCtrl = this.searchForm.controls['specLocType'];
    const expiredStatusCtrl = this.searchForm.controls['expiredStatus'];
    const nameOfPersonCtrl = this.searchForm.controls['nameOfPerson'];
    const costCenterCtrl = this.searchForm.controls['costCenter'];
    const employeeIDCtrl = this.searchForm.controls['employeeID'];

    if (plantCtrl.invalid || specLocTypeCtrl.invalid || expiredStatusCtrl.invalid) {
      this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFields'));
      return;
    }

    const expiredStatus = expiredStatusCtrl.value;
    if (expiredStatus === '30' || expiredStatus === '60') {
      return;
    }

    if (isNullOrEmpty(nameOfPersonCtrl.value) || isNullOrEmpty(costCenterCtrl.value) || isNullOrEmpty(employeeIDCtrl.value)) {
      this.confirmationService.confirm({
        message: this.translateService.instant('Common.Toast.CheckRequiredFields'),
        header: this.translateService.instant('Common.Dialog.Header'),
        icon: 'pi pi-exclamation-triangle',
        rejectVisible: false,
        acceptLabel: this.translateService.instant('Common.Button.Confirm')
      });
      return;
    }

    if (this.selectedRows.length === 0) {
      this.toastService.warn(this.translateService.instant('Common.Toast.NoDetailsSelected'));
      return;
    }

    this.confirmationService.confirm({
      message: this.translateService.instant('ExpiredControl.Message.ConfirmSend311'),
      header: this.translateService.instant('Common.Dialog.Header'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.executeSend311();
      }
    });
  }

  private executeSend311(): void {
    this.isLoading = true;

    const formValue = this.searchForm.value;
    const currentYear = new Date().getFullYear().toString();

    // Reconstruct stocks from selected SKU-level rows
    const stockMap = new Map<string, any>();
    this.selectedRows.forEach((row: any) => {
      if (!stockMap.has(row.sku)) {
        stockMap.set(row.sku, {
          sku: row.sku,
          totalQty: row.totalQty,
          stockDs: []
        });
      }
      (row.details || []).forEach((detail: any) => {
        stockMap.get(row.sku).stockDs.push({
          qty: detail.qty,
          fromLoc: detail.fromLoc,
          toLoc: detail.toLoc,
          toSpecLocType: detail.toSpecLocType,
          uId: detail.uId
        });
      });
    });

    const stocks = Array.from(stockMap.values());

    const request: ExpiredControlSend311Request = {
      plant: formValue.plant,
      materialDocumentYear: currentYear,
      movementType: '311',
      fromSpecLocType: formValue.specLocType,
      employeeID: formValue.employeeID,
      costCenter: formValue.costCenter,
      dataSourceSystem: 'S',
      orderType: '',
      nameOfPerson: formValue.nameOfPerson,
      expiredStatus: formValue.expiredStatus,
      stocks: stocks
    };

    this.expiredControlService.send311ExpiredInventory(request)
      .subscribe({
        next: () => {
          this.toastService.success(this.translateService.instant('Common.Toast.Success'));
          this.searchResult();
        },
        error: (err: any) => {
          this.toastErrorMessage(err.error);
          this.isLoading = false;
        }
      });
  }

}

